import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { renderRoutes } from 'react-router-config';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';

import { UiProvider } from '@xsolla/ui-core';
import { ModalProvider } from '@xsolla/ui-modal';

import * as Sentry from '@sentry/react';

import {
  LoginProjectConfiguration,
  MerchantProjectsResponse,
  setHTTPToken,
  SocialConnectionsResponse
} from '../../api';
import { BabkaOauthSettings } from '../../api/configurations/babkaLogin/types';
import { CheckListItems, LoginProjectData } from '../../api/dasboard/types';
import { AppProvider } from '../../contexts/config';
import { SettingsContext } from '../../contexts/settings';
import { AppSettings } from '../../contexts/settings/types';
import { insideRoutes } from '../../routes';
import { XsollaAnalytics } from '../../types/publisher';
import { getTranslationsData, GlobalStyle, parseJWT } from '../../utils';
import { initDatadogRum } from '../../utils/datadog';
import { NotificationList, SentryError } from '../ui';
import { NotificationsState } from '../ui/NotificationList/types';
import sendErrorToSentry from './sendErrorToSentry';

/**
 * Initializing Datadog RUM
 */
initDatadogRum();

interface AppProps {
  isSeparatedMode?: boolean;
  merchantId: number;
  projectId: number;
  locale: string;
  token: string;
  rootURL: string;
  history?: RouteComponentProps['history'];
  xsollaAnalytics?: XsollaAnalytics;
}

const defaultAnalytics: XsollaAnalytics = {
  sendClick: (name, value, extraValue) => {
    console.log('=xa.click name', name); // eslint-disable-line no-console
    console.log('=xa.click value', value); // eslint-disable-line no-console
    console.log('=xa.click extraValue', extraValue); // eslint-disable-line no-console
  },
  sendOpen: (name, value, extraValue) => {
    console.log('=xa.open name', name); // eslint-disable-line no-console
    console.log('=xa.open value', value); // eslint-disable-line no-console
    console.log('=xa.open extraValue', extraValue); // eslint-disable-line no-console
  },
  sendExternalLink: () => undefined
};

const App: FC<AppProps> = (props: AppProps) => {
  const {
    isSeparatedMode = false,
    merchantId,
    projectId,
    locale,
    token,
    rootURL: rawRootURL,
    history,
    xsollaAnalytics
  } = props;
  const rootURL = (rawRootURL.startsWith('/') ? rawRootURL : `/${rawRootURL}`).replace(/\/+$/, '');

  const [isTokenKept, keepToken] = useState<boolean>(false);
  const [labels] = useState(getTranslationsData(locale));
  const [appSettings, setAppSettings] = useState<AppSettings>({ isTabsShown: false });
  const [projects, setProjects] = useState<Array<LoginProjectData> | null>(null);
  const [checklistItems, setChecklistItems] = useState<CheckListItems | null>(null);
  const [currentProject, setCurrentProject] = useState<LoginProjectConfiguration | null>(null);
  const [currentSocials, setCurrentSocials] = useState<SocialConnectionsResponse | null>(null);
  const [platformSettings, setPlatformSettings] = useState<SocialConnectionsResponse | null>(null);
  const [merchantProjects, setMerchantProjects] = useState<MerchantProjectsResponse | null>(null);
  const [notifyState, setNotifyState] = useState<NotificationsState>([]);
  const [babkaLoginClient, setBabkaLoginClient] = useState<BabkaOauthSettings[] | null>(null);
  const [showBabkaLogin, setShowBabkaLogin] = useState<boolean>(false);

  moment.locale(locale);

  useEffect(() => {
    if (token) {
      setHTTPToken(token);
      setAppSettings((prevState) => ({ ...prevState, user: parseJWT(token) }));
      keepToken(true);
    }
  }, [token]);

  return (
    <AppProvider
      merchantId={merchantId}
      projectId={projectId}
      rootURL={rootURL}
      history={history}
      locale={locale}
      xsollaAnalytics={xsollaAnalytics || defaultAnalytics}>
      <UiProvider locale={locale}>
        <GlobalStyle />
        <SettingsContext.Provider
          value={{
            appSettings,
            setAppSettings,
            projects,
            setProjects,
            currentProject,
            setCurrentProject,
            currentSocials,
            setCurrentSocials,
            platformSettings,
            setPlatformSettings,
            merchantProjects,
            setMerchantProjects,
            notifyState,
            setNotifyState,
            checklistItems,
            setChecklistItems,
            babkaLoginClient,
            setBabkaLoginClient,
            showBabkaLogin,
            setShowBabkaLogin
          }}>
          <IntlProvider key={locale} locale={locale} messages={labels}>
            <Sentry.ErrorBoundary
              onError={sendErrorToSentry}
              fallback={(sentryProps) => SentryError({ ...sentryProps, history, labels })}>
              <NotificationList />
              <ModalProvider>
                <Switch>
                  {isTokenKept && insideRoutes && renderRoutes(insideRoutes(rootURL).routes)}
                  {isSeparatedMode && (
                    <Route path="/not-found" component={() => <div>404: Missing page</div>} />
                  )}
                </Switch>
              </ModalProvider>
            </Sentry.ErrorBoundary>
          </IntlProvider>
        </SettingsContext.Provider>
      </UiProvider>
    </AppProvider>
  );
};

export default App;
